import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import ApiService from "../../../services/ApiService";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { sharedStyles, SectionHead } from "./_shared";
import { resolveImage } from "../utils";

const styles = `
.t26-articles__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(16px, 2.5vw, 24px);
  /* Égalise la hauteur des cards sur la même ligne */
  grid-auto-rows: 1fr;
  align-items: stretch;
}
.t26-article-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  display: flex; flex-direction: column;
  height: 100%;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.t26-article-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
  border-color: color-mix(in oklab, var(--primary) 35%, var(--line));
}
/* Container media en aspect-ratio fixe : robuste, peu importe l'image source.
   On utilise position: absolute pour forcer le cover dans tous les cas. */
.t26-article-card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: color-mix(in oklab, var(--primary) 12%, var(--bg-2));
  overflow: hidden;
  flex-shrink: 0;
}
.t26-article-card__media img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.t26-article-card__body {
  padding: 18px;
  display: flex; flex-direction: column; gap: 10px;
  flex: 1;
}
.t26-article-card__tag {
  font-size: 10px; font-weight: 700;
  letter-spacing: .18em; text-transform: uppercase;
  color: var(--secondary);
}
.t26-article-card__title {
  font-family: var(--font-display);
  font-size: 18px; font-weight: 700;
  line-height: 1.25;
  color: var(--ink);
  margin: 0;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.t26-article-card__date {
  font-size: 12px; color: var(--ink-3);
  margin-top: auto;
}
`;

export default function ArticlesPreview({ context, limit = 3, eyebrow, title, subtitle }) {
    const { org, langValue } = context;
    const [data, setData] = React.useState([]);

    React.useEffect(() => {
        if (org?.limited) return;
        let cancelled = false;
        (async () => {
            try {
                const lv = langValue || context.lang?.value || "fr";
                const res = await ApiService.get("/auto-content/preview?lang=" + lv);
                if (!cancelled && Array.isArray(res?.data)) {
                    setData(res.data.slice(0, limit));
                }
            } catch (err) {
                // silencieux : preview optionnelle
            }
        })();
        return () => { cancelled = true; };
    }, [org?._id, langValue, limit]);

    if (org?.limited || data.length === 0) return null;

    const hasContentHub = (org?.pages || []).some(p => p.type === "CONTENT_HUB");
    const allArticlesHref = `${org.BASE_WEB_URL}/jewish-educations`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section className="t26-section" style={{ textAlign: "center" }}>
                <SectionHead
                    eyebrow={eyebrow || t`Inspirations`}
                    title={title || t`Étude et actualités`}
                    subtitle={subtitle || t`Articles, enseignements et nouvelles de notre communauté`}
                />

                <div className="t26-articles__grid">
                    {data.map(item => {
                        const isExternal = item.source && item.source !== "LOUBAVITCH";
                        const url = isExternal
                            ? item.source_link
                            : `${org.BASE_WEB_URL}/article/${item._id}`;
                        const credit = Array.isArray(item.credits) && item.credits.length > 0 ? item.credits[0] : null;
                        const inner = (
                            <div className="t26-article-card">
                                <div className="t26-article-card__media">
                                    {item.image && <img src={item.image} alt={item.title || ""} loading="lazy" />}
                                </div>
                                <div className="t26-article-card__body">
                                    {credit && <span className="t26-article-card__tag">{credit.name}</span>}
                                    <h3 className="t26-article-card__title">{item.title}</h3>
                                    {item.datetime && (
                                        <span className="t26-article-card__date">
                                            {new Date(item.datetime).toLocaleDateString(langValue || "fr")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                        if (isExternal) {
                            return <a key={item._id} href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{inner}</a>;
                        }
                        return <Link key={item._id} href={url} legacyBehavior><a style={{ textDecoration: "none" }}>{inner}</a></Link>;
                    })}
                </div>

                {hasContentHub && (
                    <Link href={allArticlesHref} legacyBehavior>
                        <a className="t26-section__more">
                            <span><Trans>Voir plus d'articles</Trans></span>
                            <span className="t26-section__more-arrow" aria-hidden="true">→</span>
                        </a>
                    </Link>
                )}
            </section>
        </>
    );
}
